import dayjs from 'dayjs';
import { IMessage } from 'react-chat-elements';
import { ConversationMessage } from 'services/api/models/ConversationDTO';
import { GGApiMessage, GGApiSender } from 'services/api/models/GGApiMessage';
import { UserDTO } from 'services/api/models/UserDTO';
import { MessageSummary } from 'store/conversations/types';
import defaultCaregiverPhoto from '../assets/experts-man-dark.png';
import { colors } from 'design';

export type ChatMessage = IMessage &
  GGApiMessage & {
    messageId: number;
    isDeleted?: boolean;
    editedAt?: string | null;
  };

function messageStatus(message: ConversationMessage, currentUser: UserDTO, conversationSummary: MessageSummary) {
  if (currentUser?.is_caregiver) {
    const readStatus = conversationSummary?.conversation.read_statuses?.filter(
      status => status.reader === conversationSummary?.conversation.patient.id,
    );
    if (readStatus?.length && readStatus[0].last_read_datetime >= message.sent_at) {
      return 'read';
    }
  }
  return 'sent';
}

export default class MessageUtil {
  static convertMessagesToComponentDataType(
    data: ConversationMessage[],
    currentUser: UserDTO,
    conversationSummary: MessageSummary,
    deletedMessageLabel = 'Ce message a été supprimé',
  ): ChatMessage[] {
    return data.map((message, index) => {
      const isSenderMe = message.sender?.id === currentUser?.id;
      const title = isSenderMe ? null : message.sender?.display_name;
      const position = isSenderMe ? 'right' : 'left';
      const isSentFromOtherCaregiver = !isSenderMe && message.sender?.is_caregiver;
      const titleColor = isSenderMe ? colors.chat.me.title : colors.chat.them.title;

      const getSenderCaregiverPhoto = (sender?: GGApiSender) =>
        sender?.is_caregiver ? sender?.photo_url || defaultCaregiverPhoto : sender?.photo_url;

      return {
        id: index.toString(),
        messageId: message.id,
        date: dayjs(message.sent_at).fromNow(),
        dateString: dayjs(message.sent_at).fromNow(),
        status: messageStatus(message, currentUser, conversationSummary),
        sender: {
          id: message.sender.id,
          display_name: message.sender.display_name,
          photo_url: message.sender.photo_url,
          is_caregiver: message.sender.is_caregiver,
        },
        text: message.message_text,
        type: 'text' as const,
        title,
        position,
        titleColor,
        className:
          currentUser?.is_caregiver && isSentFromOtherCaregiver ? 'message-sent-from-other-caregiver' : undefined,
        avatar: isSenderMe ? null : getSenderCaregiverPhoto(message.sender),
        focus: false,
        forwarded: false,
        // We implement swipe-to-reply; disable react-chat-elements' built-in reply button.
        replyButton: false,
        removeButton: false,
        notch: false,
        retracted: false,
        isDeleted: message.is_deleted,
        editedAt: message.edited_at,
        reply:
          message.replied_to_message && !message.is_deleted
            ? {
                id: message.replied_to_message.id,
                message: message.replied_to_message.is_deleted
                  ? deletedMessageLabel
                  : message.replied_to_message.message_text,
                title: message.replied_to_message.sender.display_name,
                titleColor: colors.chat.them.title,
              }
            : undefined,
      } as ChatMessage;
    });
  }
}
